import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Layers, Plus, Search, Edit, Trash2, Eye, Package, Calendar, Loader2, Truck, ImageIcon, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { rawItemApiService, RawItem, RawItemCreateRequest } from "../services";
import { itemCategoryApiService, ItemCategory } from "../services/itemCategoryApi";
import { hsnApiService, HsnCode } from "../services/hsnApi";
import { PermissionGate } from "./ui/PermissionGate";
import { useAuth } from "../contexts/AuthContext";
import { resolveApiAssetUrl } from "../config/api";
import {
  parseRawItemBulkFile,
  revokeBulkPreviewUrls,
  RawItemBulkRow,
} from "../utils/rawItemBulkImport";

type BulkRowStatus = 'pending' | 'ready' | 'created' | 'image_failed' | 'failed' | 'skipped';

type ImageRowStatus = 'matched' | 'not_found' | 'no_image' | 'has_image' | 'updated' | 'failed';

interface ImagePreviewRow extends RawItemBulkRow {
  status: ImageRowStatus;
  statusMessage: string;
  matchedItem?: RawItem;
}

interface BulkPreviewRow extends RawItemBulkRow {
  status: BulkRowStatus;
  statusMessage: string;
  categoryId?: number;
  hsnId?: number;
  createdId?: number;
}

export function RawItemListingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20; // Increased to 20 items per page

  // Raw Item management route (from backend permissions)
  const RAW_ITEM_ROUTE = '/raw-items';

  // Bulk upload state
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [bulkWarnings, setBulkWarnings] = useState<string[]>([]);
  const [bulkRows, setBulkRows] = useState<BulkPreviewRow[]>([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState('');
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [hsnCodes, setHsnCodes] = useState<HsnCode[]>([]);
  const [defaultHsnId, setDefaultHsnId] = useState<string>('');
  const [defaultUnit, setDefaultUnit] = useState('Nos');

  // Image-only upload state (existing items)
  const [isImgOpen, setIsImgOpen] = useState(false);
  const [imgFileName, setImgFileName] = useState('');
  const [imgError, setImgError] = useState('');
  const [imgWarnings, setImgWarnings] = useState<string[]>([]);
  const [imgRows, setImgRows] = useState<ImagePreviewRow[]>([]);
  const [imgUpdating, setImgUpdating] = useState(false);
  const [imgProgress, setImgProgress] = useState('');

  // Load raw items from API
  useEffect(() => {
    loadRawItems();
  }, []);

  useEffect(() => {
    if (!isBulkOpen) return;
    const loadLookups = async () => {
      try {
        const [cats, hsns] = await Promise.all([
          itemCategoryApiService.getItemCategories(),
          hsnApiService.getHsnCodes(),
        ]);
        setCategories(cats);
        setHsnCodes(hsns);
      } catch {
        toast.error('Failed to load categories / HSN codes for bulk upload');
      }
    };
    loadLookups();
  }, [isBulkOpen]);

  useEffect(() => {
    return () => {
      revokeBulkPreviewUrls(bulkRows);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRawItems = async () => {
    try {
      setLoading(true);
      const rawItemsData = await rawItemApiService.getRawItems();
      setRawItems(rawItemsData);
    } catch (error) {
      // console.error('Error loading raw items:', error);
      toast.error('Failed to load raw items');
    } finally {
      setLoading(false);
    }
  };

  const categoryByName = useMemo(() => {
    const map = new Map<string, ItemCategory>();
    categories.forEach((c) => {
      const key = (c.categoryName || '').trim().toLowerCase();
      if (key) map.set(key, c);
    });
    return map;
  }, [categories]);

  const hsnByCode = useMemo(() => {
    const map = new Map<string, HsnCode>();
    hsnCodes.forEach((h) => {
      const key = (h.hsnCode || '').trim().toLowerCase();
      if (key) map.set(key, h);
    });
    return map;
  }, [hsnCodes]);

  const existingNames = useMemo(() => {
    const set = new Set<string>();
    rawItems.forEach((item) => {
      const key = (item.itemName || '').trim().toLowerCase();
      if (key) set.add(key);
    });
    return set;
  }, [rawItems]);

  const enrichBulkRows = (rows: RawItemBulkRow[]): BulkPreviewRow[] => {
    return rows.map((row) => {
      const nameKey = row.itemName.trim().toLowerCase();
      const category = categoryByName.get(row.categoryName.trim().toLowerCase());
      const hsnFromRow = row.hsnCode
        ? hsnByCode.get(row.hsnCode.trim().toLowerCase())
        : undefined;
      const fallbackHsnId = defaultHsnId ? parseInt(defaultHsnId, 10) : undefined;
      const hsnId = hsnFromRow?.id ?? (Number.isFinite(fallbackHsnId) ? fallbackHsnId : undefined);

      if (!row.itemName.trim()) {
        return { ...row, status: 'failed', statusMessage: 'Missing item name', categoryId: category?.id, hsnId };
      }
      if (!category) {
        return { ...row, status: 'failed', statusMessage: `Category not found: ${row.categoryName || '—'}`, hsnId };
      }
      if (!hsnId) {
        return {
          ...row,
          status: 'failed',
          statusMessage: row.hsnCode
            ? `HSN not found: ${row.hsnCode}`
            : 'Select a default HSN (or provide HSN column)',
          categoryId: category.id,
        };
      }
      if (nameKey && existingNames.has(nameKey)) {
        return {
          ...row,
          status: 'skipped',
          statusMessage: 'Item name already exists',
          categoryId: category.id,
          hsnId,
        };
      }
      return {
        ...row,
        status: 'ready',
        statusMessage: row.imageFile ? 'Ready (with image)' : 'Ready (no image)',
        categoryId: category.id,
        hsnId,
      };
    });
  };

  // Re-validate rows that have not been imported yet when lookups / default HSN change
  useEffect(() => {
    if (bulkRows.length === 0) return;
    setBulkRows((prev) =>
      prev.map((row) => {
        if (row.status === 'created' || row.status === 'image_failed') {
          return row;
        }
        const base: RawItemBulkRow = {
          rowIndex: row.rowIndex,
          itemName: row.itemName,
          categoryName: row.categoryName,
          unit: row.unit,
          hsnCode: row.hsnCode,
          imageFile: row.imageFile,
          imagePreviewUrl: row.imagePreviewUrl,
        };
        return enrichBulkRows([base])[0];
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, hsnCodes, defaultHsnId, existingNames]);

  const resetBulkState = () => {
    revokeBulkPreviewUrls(bulkRows);
    setBulkRows([]);
    setBulkFileName('');
    setBulkError('');
    setBulkWarnings([]);
    setBulkProgress('');
  };

  const handleBulkDialogChange = (open: boolean) => {
    setIsBulkOpen(open);
    if (!open) {
      resetBulkState();
    }
  };

  const handleBulkFileSelected = async (file: File | null) => {
    revokeBulkPreviewUrls(bulkRows);
    setBulkRows([]);
    setBulkError('');
    setBulkWarnings([]);
    setBulkFileName(file?.name || '');
    if (!file) return;

    try {
      const parsed = await parseRawItemBulkFile(file);
      setBulkWarnings(parsed.warnings);
      setBulkRows(enrichBulkRows(parsed.rows));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to parse file';
      setBulkError(message);
    }
  };

  const downloadSampleCsv = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL || ''}/samples/raw-items-bulk-sample.csv`;
    link.download = 'raw-items-bulk-sample.csv';
    link.click();
  };

  const itemByName = useMemo(() => {
    const map = new Map<string, RawItem>();
    rawItems.forEach((item) => {
      const key = (item.itemName || '').trim().toLowerCase();
      if (key && !map.has(key)) map.set(key, item);
    });
    return map;
  }, [rawItems]);

  const matchImageRows = (rows: RawItemBulkRow[]): ImagePreviewRow[] =>
    rows.map((row) => {
      const matchedItem = itemByName.get(row.itemName.trim().toLowerCase());
      if (!matchedItem) {
        return { ...row, status: 'not_found', statusMessage: 'Item not found' };
      }
      if (!row.imageFile) {
        return { ...row, status: 'no_image', statusMessage: 'No image in file', matchedItem };
      }
      if (matchedItem.imageUrl) {
        return { ...row, status: 'has_image', statusMessage: 'Skipped - item already has image', matchedItem };
      }
      return { ...row, status: 'matched', statusMessage: 'Found - will add image', matchedItem };
    });

  const resetImgState = () => {
    revokeBulkPreviewUrls(imgRows);
    setImgRows([]);
    setImgFileName('');
    setImgError('');
    setImgWarnings([]);
    setImgProgress('');
  };

  const handleImgDialogChange = (open: boolean) => {
    setIsImgOpen(open);
    if (!open) resetImgState();
  };

  const handleImgFileSelected = async (file: File | null) => {
    revokeBulkPreviewUrls(imgRows);
    setImgRows([]);
    setImgError('');
    setImgWarnings([]);
    setImgFileName(file?.name || '');
    if (!file) return;
    try {
      const parsed = await parseRawItemBulkFile(file, { requireCategory: false });
      setImgWarnings(parsed.warnings);
      setImgRows(matchImageRows(parsed.rows));
    } catch (error) {
      setImgError(error instanceof Error ? error.message : 'Failed to parse file');
    }
  };

  const imgMatchedCount = imgRows.filter((r) => r.status === 'matched').length;

  const handleImageUpdate = async () => {
    if (imgMatchedCount === 0) {
      toast.error('No matched rows with images to update');
      return;
    }
    setImgUpdating(true);
    let updated = 0;
    let failed = 0;
    try {
      for (let i = 0; i < imgRows.length; i++) {
        const row = imgRows[i];
        if (row.status !== 'matched' || !row.matchedItem || !row.imageFile) continue;
        setImgProgress(`Updating ${i + 1} of ${imgRows.length}: ${row.itemName}`);
        try {
          await rawItemApiService.uploadRawItemImage(row.matchedItem.id, row.imageFile, { silent: true });
          updated += 1;
          setImgRows((prev) =>
            prev.map((r, idx) => (idx === i ? { ...r, status: 'updated', statusMessage: 'Image updated' } : r))
          );
        } catch (error) {
          failed += 1;
          const message = error instanceof Error ? error.message : 'Upload failed';
          setImgRows((prev) =>
            prev.map((r, idx) => (idx === i ? { ...r, status: 'failed', statusMessage: message } : r))
          );
        }
      }
      await loadRawItems();
      toast.success(`Image update finished: ${updated} updated` + (failed ? `, ${failed} failed` : ''));
    } finally {
      setImgUpdating(false);
      setImgProgress('');
    }
  };

  const readyCount = bulkRows.filter((r) => r.status === 'ready').length;

  const handleBulkImport = async () => {
    const toImport = bulkRows.filter((r) => r.status === 'ready');
    if (toImport.length === 0) {
      toast.error('No ready rows to import');
      return;
    }
    if (!user?.id) {
      toast.error('Unable to identify the logged-in user. Please log in again.');
      return;
    }

    setBulkImporting(true);
    let created = 0;
    let imageFailed = 0;
    let failed = 0;

    try {
      for (let i = 0; i < bulkRows.length; i++) {
        const row = bulkRows[i];
        if (row.status !== 'ready') continue;

        setBulkProgress(`Importing ${i + 1} of ${bulkRows.length}: ${row.itemName}`);
        const payload: RawItemCreateRequest = {
          itemName: row.itemName.trim(),
          itemUnit: (row.unit || defaultUnit || 'Nos').trim() || 'Nos',
          sku: '',
          color: '',
          shape: '',
          size: '',
          grade: '',
          weight: 0,
          minBufferStock: 0,
          defaultUnitPrice: 0,
          isActive: true,
          itemCategoryId: row.categoryId!,
          hsnId: row.hsnId!,
          createdBy: user.id,
          updatedBy: user.id,
        };

        try {
          const createdItem = await rawItemApiService.createRawItem(payload, { silent: true });
          created += 1;

          if (row.imageFile) {
            try {
              await rawItemApiService.uploadRawItemImage(createdItem.id, row.imageFile, { silent: true });
              setBulkRows((prev) =>
                prev.map((r, idx) =>
                  idx === i
                    ? { ...r, status: 'created', statusMessage: 'Created with image', createdId: createdItem.id }
                    : r
                )
              );
            } catch {
              imageFailed += 1;
              setBulkRows((prev) =>
                prev.map((r, idx) =>
                  idx === i
                    ? {
                        ...r,
                        status: 'image_failed',
                        statusMessage: 'Created, but image upload failed',
                        createdId: createdItem.id,
                      }
                    : r
                )
              );
            }
          } else {
            setBulkRows((prev) =>
              prev.map((r, idx) =>
                idx === i
                  ? { ...r, status: 'created', statusMessage: 'Created', createdId: createdItem.id }
                  : r
              )
            );
          }
        } catch (error) {
          failed += 1;
          const message = error instanceof Error ? error.message : 'Create failed';
          setBulkRows((prev) =>
            prev.map((r, idx) =>
              idx === i ? { ...r, status: 'failed', statusMessage: message } : r
            )
          );
        }
      }

      await loadRawItems();
      toast.success(
        `Bulk import finished: ${created} created` +
          (imageFailed ? `, ${imageFailed} image upload failed` : '') +
          (failed ? `, ${failed} failed` : '')
      );
    } finally {
      setBulkImporting(false);
      setBulkProgress('');
    }
  };

  // Filter raw items based on search term
  const filteredRawItems = rawItems.filter(item => 
    item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.itemCategory?.categoryName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total pages based on filtered items
  const totalPages = Math.ceil(filteredRawItems.length / pageSize);
  const totalCount = filteredRawItems.length;

  // Pagination logic - slice the filtered items
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRawItems = filteredRawItems.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewRawItem = () => {
    navigate('/raw-items/new');
  };

  const handleEditRawItem = (item: RawItem) => {
    navigate(`/raw-items/${item.id}`);
  };

  const handleViewRawItem = (item: RawItem) => {
    navigate(`/raw-items/${item.id}?view=true`);
  };

  const handleDeleteRawItem = async (item: RawItem) => {
    if (window.confirm(`Are you sure you want to delete ${item.itemName}?`)) {
      try {
        await rawItemApiService.deleteRawItem(item.id.toString());
        await loadRawItems(); // Reload the list
        toast.success('Raw item deleted successfully');
      } catch (error) {
        // console.error('Error deleting raw item:', error);
        toast.error('Failed to delete raw item');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Raw Item Management</CardTitle>
                <p className="text-primary-100 text-sm">Manage your raw materials and inventory</p>
              </div>
            </div>
            <PermissionGate route={RAW_ITEM_ROUTE} action="create">
              <div className="flex items-center gap-2">
                <Dialog open={isBulkOpen} onOpenChange={handleBulkDialogChange}>
                  {/* TEMPORARY: Bulk Upload button hidden
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Bulk Upload
                    </Button>
                  </DialogTrigger>
                  */}
                  <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Bulk upload raw items</DialogTitle>
                      <DialogDescription>
                        Upload <span className="font-medium">.xlsx</span> (recommended for images) or{' '}
                        <span className="font-medium">.csv</span> with columns{' '}
                        <span className="font-medium">item name</span>,{' '}
                        <span className="font-medium">category</span>, optional{' '}
                        <span className="font-medium">unit</span> / <span className="font-medium">hsn</span>.
                        Put pictures in an <span className="font-medium">Image</span> column for Excel.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-2 flex-1 min-w-[200px]">
                          <Label>File (.xlsx / .csv)</Label>
                          <Input
                            type="file"
                            accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                            disabled={bulkImporting}
                            onChange={(e) => handleBulkFileSelected(e.target.files?.[0] || null)}
                          />
                          {bulkFileName && (
                            <p className="text-xs text-muted-foreground">Selected: {bulkFileName}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={downloadSampleCsv}
                          disabled={bulkImporting}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Sample CSV
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Default HSN (used when row has no HSN)</Label>
                          <Select
                            value={defaultHsnId}
                            onValueChange={setDefaultHsnId}
                            disabled={bulkImporting}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select default HSN" />
                            </SelectTrigger>
                            <SelectContentWithSearch searchPlaceholder="Search HSN...">
                              {hsnCodes.map((hsn) => (
                                <SelectItem key={hsn.id} value={hsn.id.toString()}>
                                  {hsn.hsnCode}
                                </SelectItem>
                              ))}
                            </SelectContentWithSearch>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Default unit (when row has no unit)</Label>
                          <Input
                            value={defaultUnit}
                            onChange={(e) => setDefaultUnit(e.target.value)}
                            disabled={bulkImporting}
                            placeholder="Nos"
                          />
                        </div>
                      </div>

                      {bulkError && <p className="text-sm text-red-600">{bulkError}</p>}
                      {bulkWarnings.length > 0 && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 space-y-1">
                          {bulkWarnings.map((w, i) => (
                            <p key={`${i}-${w}`}>{w}</p>
                          ))}
                        </div>
                      )}
                      {bulkProgress && (
                        <p className="text-sm text-primary-700 flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {bulkProgress}
                        </p>
                      )}

                      {bulkRows.length > 0 && (
                        <div className="border border-primary-200 rounded-lg overflow-hidden">
                          <div className="max-h-96 overflow-auto">
                            <Table>
                              <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 sticky top-0">
                                <TableRow>
                                  <TableHead className="w-14">Row</TableHead>
                                  <TableHead className="w-16">Image</TableHead>
                                  <TableHead>Item name</TableHead>
                                  <TableHead>Category</TableHead>
                                  <TableHead className="w-20">Unit</TableHead>
                                  <TableHead className="w-28">HSN</TableHead>
                                  <TableHead className="w-48">Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {bulkRows.map((row) => {
                                  const ok =
                                    row.status === 'ready' ||
                                    row.status === 'created';
                                  const warn = row.status === 'image_failed' || row.status === 'skipped';
                                  return (
                                    <TableRow
                                      key={`${row.rowIndex}-${row.itemName}`}
                                      className={
                                        ok
                                          ? 'bg-emerald-50/40'
                                          : warn
                                            ? 'bg-amber-50/40'
                                            : 'bg-red-50/30'
                                      }
                                    >
                                      <TableCell className="text-xs text-muted-foreground">
                                        {row.rowIndex}
                                      </TableCell>
                                      <TableCell>
                                        <div className="h-10 w-10 rounded border bg-gray-50 overflow-hidden flex items-center justify-center">
                                          {row.imagePreviewUrl ? (
                                            <img
                                              src={row.imagePreviewUrl}
                                              alt=""
                                              className="h-full w-full object-cover"
                                            />
                                          ) : (
                                            <ImageIcon className="h-4 w-4 text-gray-400" />
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="font-medium">{row.itemName || '—'}</TableCell>
                                      <TableCell>{row.categoryName || '—'}</TableCell>
                                      <TableCell>{row.unit || defaultUnit || '—'}</TableCell>
                                      <TableCell>
                                        {row.hsnCode ||
                                          hsnCodes.find((h) => h.id.toString() === defaultHsnId)?.hsnCode ||
                                          '—'}
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          className={
                                            row.status === 'ready' || row.status === 'created'
                                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                              : row.status === 'image_failed' || row.status === 'skipped'
                                                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                                : 'bg-red-100 text-red-800 border border-red-200'
                                          }
                                        >
                                          {row.statusMessage}
                                        </Badge>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                          <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-white">
                            {readyCount} ready of {bulkRows.length} rows
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleBulkDialogChange(false)}
                          disabled={bulkImporting}
                        >
                          Close
                        </Button>
                        <Button
                          onClick={handleBulkImport}
                          disabled={bulkImporting || readyCount === 0}
                          className="bg-primary-500 text-white hover:bg-primary-600"
                        >
                          {bulkImporting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            `Import ${readyCount} item${readyCount === 1 ? '' : 's'}`
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={isImgOpen} onOpenChange={handleImgDialogChange}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white"
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Upload Images
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                    <DialogHeader>
                      <DialogTitle>Upload images for existing items</DialogTitle>
                      <DialogDescription>
                        Upload an <span className="font-medium">.xlsx</span> with an{' '}
                        <span className="font-medium">item name</span> column and pictures in an{' '}
                        <span className="font-medium">Image</span> column. Items are matched by name; review the
                        comparison below, then confirm to update. Only items without an image get one; existing images are
                        never replaced. No new items are created.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 flex-1 min-h-0 flex flex-col">
                      <div className="space-y-2">
                        <Label>File (.xlsx)</Label>
                        <Input
                          type="file"
                          accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                          disabled={imgUpdating}
                          onChange={(e) => handleImgFileSelected(e.target.files?.[0] || null)}
                        />
                        {imgFileName && <p className="text-xs text-muted-foreground">Selected: {imgFileName}</p>}
                        {imgError && <p className="text-sm text-red-600">{imgError}</p>}
                        {imgWarnings.map((w, i) => (
                          <p key={i} className="text-xs text-amber-700">{w}</p>
                        ))}
                      </div>

                      {imgRows.length > 0 && (
                        <div className="border border-primary-200 rounded-lg overflow-auto flex-1 min-h-0">
                          <Table>
                            <TableHeader className="bg-primary-50">
                              <TableRow>
                                <TableHead className="w-14">Row</TableHead>
                                <TableHead>Item name</TableHead>
                                <TableHead className="w-24">Current</TableHead>
                                <TableHead className="w-24">New</TableHead>
                                <TableHead className="w-56">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {imgRows.map((row) => {
                                const ok = row.status === 'matched' || row.status === 'updated';
                                const warn = row.status === 'no_image' || row.status === 'has_image';
                                const currentUrl = row.matchedItem?.imageUrl
                                  ? resolveApiAssetUrl(row.matchedItem.imageUrl)
                                  : null;
                                return (
                                  <TableRow
                                    key={`${row.rowIndex}-${row.itemName}`}
                                    className={ok ? 'bg-emerald-50/40' : warn ? 'bg-amber-50/40' : 'bg-red-50/30'}
                                  >
                                    <TableCell className="text-xs text-muted-foreground">{row.rowIndex}</TableCell>
                                    <TableCell className="font-medium">{row.itemName || '—'}</TableCell>
                                    <TableCell>
                                      <div className="h-10 w-10 rounded border bg-gray-50 overflow-hidden flex items-center justify-center">
                                        {currentUrl ? (
                                          <img src={currentUrl} alt="" className="h-full w-full object-cover" />
                                        ) : (
                                          <ImageIcon className="h-4 w-4 text-gray-400" />
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="h-10 w-10 rounded border bg-gray-50 overflow-hidden flex items-center justify-center">
                                        {row.imagePreviewUrl ? (
                                          <img src={row.imagePreviewUrl} alt="" className="h-full w-full object-cover" />
                                        ) : (
                                          <ImageIcon className="h-4 w-4 text-gray-400" />
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        className={
                                          ok
                                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                            : warn
                                              ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                              : 'bg-red-100 text-red-800 border border-red-200'
                                        }
                                      >
                                        {row.statusMessage}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                      {imgRows.length > 0 && (
                        <p className="text-xs text-muted-foreground shrink-0">
                          {imgMatchedCount} of {imgRows.length} rows will add an image to an item that has none
                          {imgProgress && ` — ${imgProgress}`}
                        </p>
                      )}

                      <div className="flex items-center justify-end gap-2 shrink-0">
                        <Button variant="outline" onClick={() => handleImgDialogChange(false)} disabled={imgUpdating}>
                          Close
                        </Button>
                        <Button
                          onClick={handleImageUpdate}
                          disabled={imgUpdating || imgMatchedCount === 0}
                          className="bg-primary-500 text-white hover:bg-primary-600"
                        >
                          {imgUpdating ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Updating...
                            </>
                          ) : (
                            `Update ${imgMatchedCount} image${imgMatchedCount === 1 ? '' : 's'}`
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  onClick={handleNewRawItem}
                  className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Raw Item
                </Button>
              </div>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search raw items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              Showing {paginatedRawItems.length > 0 ? startIndex + 1 : 0} - {Math.min(endIndex, totalCount)} of {totalCount} items
              {searchTerm && ` (filtered from ${rawItems.length} total)`}
            </div>
          </div>

          {/* Raw Items Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <TableHead className="w-14">S No.</TableHead>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead className="w-[220px]">Item Details</TableHead>
                  <TableHead className="w-44">Category</TableHead>
                  <TableHead className="w-36">Specifications</TableHead>
                  <TableHead className="w-28">Unit & Weight</TableHead>
                  <TableHead className="w-28">Buffer Stock</TableHead>
                  <TableHead className="w-24">HSN Code</TableHead>
                  <TableHead className="w-20">Status</TableHead>
                  <TableHead className="text-center w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading raw items...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedRawItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                      No raw items found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRawItems.map((item, index) => {
                    const resolvedImageUrl = resolveApiAssetUrl(item.imageUrl);
                    return (
                    <TableRow key={item.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium w-14 whitespace-normal">{(currentPage - 1) * pageSize + index + 1}</TableCell>
                      <TableCell className="w-16 align-middle">
                        <div className="h-10 w-10 rounded border border-primary-100 bg-gray-50 overflow-hidden flex items-center justify-center">
                          {resolvedImageUrl ? (
                            <img
                              src={resolvedImageUrl}
                              alt={item.itemName || 'Raw item'}
                              loading="lazy"
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling;
                                if (fallback instanceof HTMLElement) {
                                  fallback.classList.remove('hidden');
                                }
                              }}
                            />
                          ) : null}
                          <ImageIcon
                            className={`h-4 w-4 text-gray-400 ${resolvedImageUrl ? 'hidden' : ''}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="w-[220px] overflow-hidden align-top whitespace-normal">
                        <div className="break-words pr-2 overflow-hidden">
                          <div className="font-medium break-words text-sm leading-tight whitespace-normal">{item.itemName || 'N/A'}</div>
                          <div className="text-xs text-gray-500 mt-1">SKU: {item.sku || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-44 overflow-hidden align-top whitespace-normal">
                        <Badge
                          variant="outline"
                          title={item.itemCategory?.categoryName || 'N/A'}
                          className="border-primary-200 text-primary-700 text-xs max-w-full whitespace-normal break-words h-auto shrink w-full justify-start text-left leading-snug"
                        >
                          {item.itemCategory?.categoryName || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-36 overflow-hidden align-top whitespace-normal">
                        <div className="space-y-1 text-xs">
                          <div>Type: {item.type || 'N/A'}</div>
                          <div>Color: {item.color || 'N/A'}</div>
                          <div>Shape: {item.shape || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-28 overflow-hidden align-top whitespace-normal">
                        <div className="space-y-1 text-xs">
                          <div>Unit: {item.itemUnit || 'N/A'}</div>
                          <div className="text-gray-500">Weight: {item.weight || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-28 overflow-hidden align-top whitespace-normal">
                        <div className="space-y-1 text-xs">
                          <div>Min Buffer: {item.minBufferStock || 'N/A'}</div>
                          <div className="text-gray-500">Grade: {item.grade || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-24 overflow-hidden align-top whitespace-normal">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">{item.hsn?.hsnCode || 'N/A'}</code>
                      </TableCell>
                      <TableCell className="w-20 overflow-hidden align-top whitespace-normal">
                        <Badge 
                          variant={item.isActive ? "default" : "secondary"}
                          className={item.isActive ? "bg-green-100 text-green-800 text-xs" : "bg-gray-100 text-gray-800 text-xs"}
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-28 align-top whitespace-normal">
                        <div className="flex items-center gap-2">
                          <PermissionGate route={RAW_ITEM_ROUTE} action="view">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewRawItem(item)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={RAW_ITEM_ROUTE} action="edit">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditRawItem(item)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={RAW_ITEM_ROUTE} action="delete">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteRawItem(item)}
                              className="border-red-200 hover:bg-red-50 hover:border-red-300 text-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            </div>
          </div>

          {filteredRawItems.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Layers className="h-12 w-12 mx-auto mb-3 text-primary-300" />
              <p>No raw items found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {filteredRawItems.length > 0 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Items</p>
                <p className="text-2xl font-semibold text-primary-700">{rawItems.length}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <Layers className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Stock</p>
                <p className="text-2xl font-semibold text-green-700">
                  {rawItems.filter((i: RawItem) => i.isActive).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <Package className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Low Stock</p>
                <p className="text-2xl font-semibold text-yellow-700">
                  {rawItems.filter((i: RawItem) => !i.isActive).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                <Truck className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Out of Stock</p>
                <p className="text-2xl font-semibold text-red-700">
                  {rawItems.filter((i: RawItem) => i.minBufferStock < 10).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Package className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

    </div>
  );
}